/**
 * Количества и деньги — десятичные строки, как их отдаёт API ("1015.000"), а не number:
 * 0.1 + 0.2 ≠ 0.3, а склад сверяет остатки до грамма. Арифметика — на BigInt в «копейках».
 *
 * Ввод человека: «1 234,5», «1234.5», то же с неразрывными пробелами из Excel → "1234.5".
 * Вывод ru-RU: "1234.500" → «1 234,5».
 */

/** Десятичная строка в формате API: «-», цифры, необязательная «.» с дробью. */
export type DecimalString = string;

const API_FORMAT = /^(-?)(\d+)(?:\.(\d+))?$/;
const SPACES = /[\s\u00a0\u202f]/g; // обычные, неразрывные и узкие неразрывные пробелы
const GROUP = '\u00a0'; // ru-RU разделяет разряды неразрывным пробелом

export interface ParseOptions {
  /** Сколько знаков после запятой допустимо: 3 для кг, 0 для шт. */
  places: number;
  allowNegative?: boolean;
}

/** Почему ввод не разобран; текст — в словаре qty.* (интерфейс на двух языках). */
export type ParseError =
  | { code: 'notNumber' }
  | { code: 'negative' }
  | { code: 'integer' }
  | { code: 'places'; places: number };

export type ParseResult =
  { ok: true; value: DecimalString | null } | { ok: false; error: ParseError };

/** Текст из поля ввода → строка для API. Пустое поле — `value: null`. */
export function parseDecimal(
  text: string,
  { places, allowNegative = false }: ParseOptions,
): ParseResult {
  const s = text.replace(SPACES, '').replace(',', '.');
  if (s === '') return { ok: true, value: null };
  const m = /^(-?)(\d*)(?:\.(\d*))?$/.exec(s);
  const [, sign = '', int = '', frac = ''] = m ?? [];
  if (!m || (int === '' && frac === '')) return { ok: false, error: { code: 'notNumber' } };
  if (sign && !allowNegative) return { ok: false, error: { code: 'negative' } };
  if (frac.length > places) {
    return {
      ok: false,
      error: places === 0 ? { code: 'integer' } : { code: 'places', places },
    };
  }
  const intPart = int.replace(/^0+(?=\d)/, '') || '0';
  const fracPart = frac.replace(/0+$/, '');
  const value = fracPart ? `${intPart}.${fracPart}` : intPart;
  return { ok: true, value: sign && value !== '0' ? `-${value}` : value };
}

/** Строка API → BigInt в единицах 10^-places (с округлением half-up, как в 1С). */
export function toScaled(value: DecimalString, places: number): bigint {
  const m = API_FORMAT.exec(value);
  if (!m) throw new Error(`Не десятичное число: ${value}`);
  const [, sign, int = '0', frac = ''] = m;
  const kept = frac.slice(0, places).padEnd(places, '0');
  let n = BigInt(int + kept);
  if ((frac[places] ?? '0') >= '5') n += 1n; // half-up по модулю
  return sign ? -n : n;
}

/** BigInt в единицах 10^-places → строка API без лишних нулей. */
export function fromScaled(n: bigint, places: number): DecimalString {
  const negative = n < 0n;
  const digits = (negative ? -n : n).toString().padStart(places + 1, '0');
  const int = digits.slice(0, digits.length - places);
  const frac = digits.slice(digits.length - places).replace(/0+$/, '');
  const value = frac ? `${int}.${frac}` : int;
  return negative ? `-${value}` : value;
}

/** Сумма строк — без потерь: итог по отгрузке, остаток по партиям. */
export function sumDecimals(values: readonly DecimalString[], places = 3): DecimalString {
  return fromScaled(
    values.reduce((acc, v) => acc + toScaled(v, places), 0n),
    places,
  );
}

/** Произведение строк — без потерь: «≈ N кг» = коробок × вес коробки. */
export function mulDecimals(a: DecimalString, b: DecimalString, places = 3): DecimalString {
  return fromScaled(toScaled(a, places) * toScaled(b, places), places * 2);
}

export function compareDecimals(a: DecimalString, b: DecimalString, places = 4): -1 | 0 | 1 {
  const d = toScaled(a, places) - toScaled(b, places);
  return d < 0n ? -1 : d > 0n ? 1 : 0;
}

export interface FormatOptions {
  /** Округлить до этого числа знаков (half-up). По умолчанию — как пришло. */
  maxPlaces?: number;
  /** Дополнить нулями до этого числа знаков: деньги — 2. */
  minPlaces?: number;
}

/** Строка API → «1 234,5» (ru-RU). Нераспознанная строка возвращается как есть. */
export function formatDecimal(
  value: DecimalString,
  { maxPlaces, minPlaces = 0 }: FormatOptions = {},
): string {
  if (!API_FORMAT.test(value)) return value;
  const rounded =
    maxPlaces === undefined ? value : fromScaled(toScaled(value, maxPlaces), maxPlaces);
  const [, sign = '', int = '0', frac = ''] = API_FORMAT.exec(rounded) ?? [];
  const trimmed = frac.replace(/0+$/, '').padEnd(minPlaces, '0');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP);
  const negative = sign && /[1-9]/.test(int + trimmed) ? '−' : ''; // U+2212, не дефис
  return `${negative}${grouped}${trimmed ? `,${trimmed}` : ''}`;
}
